<script setup lang="ts">
/**
 * 404 de la page publique d'association.
 *
 * UX (Karim 2026-08-05) : plus de carte boxée avec icône générique — état
 * ouvert, illustration maison « mains loupe », et surtout une VRAIE recherche
 * pré-remplie avec le nom déduit de l'URL : le visiteur cherchait une asso
 * précise, on l'aide à la retrouver en un clic (redirige vers
 * /associations?q=…). Suggestions d'assos actives en dessous
 * (pas de cul-de-sac). Le statut HTTP 404 réel est posé par la page hôte
 * (associations/[slug].vue).
 */
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// « organisation-de-jean-dupont » → « organisation de jean dupont »
const guessedName = computed(() => {
  const slug = String(
    (route.params as Record<string, string | string[]>).slug || "",
  );
  return slug.replace(/-/g, " ").trim();
});

const searchTerm = ref(guessedName.value);

function submitSearch() {
  const q = searchTerm.value.trim();
  router.push({
    path: "/recherche/associations",
    query: q ? { q } : undefined,
  });
}
</script>

<template>
  <div class="min-h-screen flex flex-col">
    <div class="flex items-center justify-center px-4 pt-44 pb-16">
      <div class="w-full max-w-lg text-center">
        <img
          src="/svg/hands-search.svg"
          alt=""
          class="mx-auto w-20 h-20 mb-4"
          loading="eager"
          width="181"
          height="181"
        />
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          {{ t("association.introuvable.titre") }}
        </h1>
        <p class="text-base text-gray-500 mb-6">
          {{ t("association.introuvable.texte") }}
        </p>

        <!-- Recherche directe, pré-remplie avec le nom déduit de l'URL -->
        <form
          class="flex items-stretch gap-2 max-w-md mx-auto"
          @submit.prevent="submitSearch"
        >
          <div class="relative flex-1">
            <UIcon
              name="i-lucide-search"
              class="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none"
            />
            <input
              v-model="searchTerm"
              type="search"
              :placeholder="t('association.introuvable.placeholderRecherche')"
              class="w-full h-12 pl-11 pr-4 rounded-full bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-main focus:ring-2 focus:ring-main/20 transition"
            />
          </div>
          <button
            type="submit"
            class="h-12 px-6 rounded-full text-sm font-semibold bg-main text-white hover:bg-main/90 active:scale-[0.98] transition whitespace-nowrap"
          >
            {{ t("association.introuvable.boutonRechercher") }}
          </button>
        </form>

        <NuxtLink
          to="/"
          class="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4" />
          {{ t("association.introuvable.retourAccueil") }}
        </NuxtLink>
      </div>
    </div>

    <!-- Suggestions d'associations actives (carousel) -->
    <NotFoundSuggestions type="associations" />
  </div>
</template>
